// src/pages/About/AboutUs.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import TopNav from "../../components/TopNav";
import BottomNav from "../../components/BottomNav";
import Footer from "../../components/footer/Footer";

// images
import makkaanLogo from "../../makkaanpages/images/makkaanlogo.png";
import development from "../../makkaanpages/images/makkaandevelopmentlogo.png";
import ninek from "../../makkaanpages/images/9K-Suits-ayubia.png";
import developerlogo from "../../makkaanpages/images/ARCLINK-SITE-LOGO.webp";
import peaknest from "../../makkaanpages/images/images.png";
import chairman from "../../makkaanpages/images/Chairman_Makkaan.png";
import hassan from "../../makkaanpages/images/sirhassan.png";
import mehmaan1 from "../../makkaanpages/images/mehmaan1.png";
import mehmaan2 from "../../makkaanpages/images/mehmaan2.png";
import mehmaan3 from "../../makkaanpages/images/mehmaan3.png";
import mehmaan4 from "../../makkaanpages/images/mehmaan4.png";
import mehmaan6 from "../../makkaanpages/images/guest_6.png";
import mehmaan7 from "../../makkaanpages/images/guest_7.png";
import mehmaan8 from "../../makkaanpages/images/guest_8.png";
import mehmaan9 from "../../makkaanpages/images/guest_9.png";
import guranteed from "../../makkaanpages/images/guaranteedstamp.png";

/* ===================== HOOK: in-view ===================== */
function useInView({ rootMargin = "200px 0px", threshold = 0.01 } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // If browser doesn't support IO, just show immediately
    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { rootMargin, threshold }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin, threshold]);

  return { ref, inView };
}

/* ===================== IMAGE WITH SKELETON ===================== */
function ImgWithSkeleton({
  src,
  alt,
  className,
  style,
  imgStyle,
  priority = false,
  ...props
}) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      className={`img-skel ${loaded ? "is-loaded" : ""} ${className || ""}`}
      style={style}
    >
      {!loaded && <div className="img-skel__shimmer" aria-hidden="true" />}
      <img
        src={src}
        alt={alt}
        className="img-skel__img"
        style={imgStyle}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fetchpriority={priority ? "high" : "auto"}
        onLoad={() => setLoaded(true)}
        onError={(e) => (e.currentTarget.style.opacity = 0)}
        {...props}
      />
    </div>
  );
}

/* ===================== SECTION LAZY MOUNT + SKELETON ===================== */
function LazySection({ skeletonHeight = 420, children, rootMargin }) {
  const { ref, inView } = useInView({ rootMargin: rootMargin || "250px 0px" });

  return (
    <div ref={ref}>
      {inView ? (
        children
      ) : (
        <div
          className="section-skeleton rounded-4"
          style={{ height: skeletonHeight }}
        >
          <div className="section-skeleton__bar w-50" />
          <div className="section-skeleton__bar w-75" />
          <div className="section-skeleton__bar w-60" />
          <div className="section-skeleton__bar w-40" />
        </div>
      )}
    </div>
  );
}

export default function AboutUs() {
  // ✅ memo so it doesn't re-create array every render
  const guests = useMemo(
    () => [
      { img: mehmaan1, name: "Talat Razeeq", role: "Afiliate markter " },
      { img: mehmaan2, name: "Sheikh sajawal ", role: "Ads King" },
      { img: mehmaan3, name: "Sohaib Ali", role: "E-Commerce Expert" },
      { img: mehmaan4, name: "Danyal saleem", role: "Real Estate Influencer" },
      { img: mehmaan7, name: "Irfan butt ", role: "Mental Health Counselor" },
      { img: mehmaan6, name: "Nouman Abid", role: "Corporate Trainer" },
      { img: mehmaan8, name: "Qasim umer", role: "Off Roading Expert" },
      { img: mehmaan9, name: "Saf khan", role: "Yt Automation Expert" },
    ],
    []
  );

  return (
    <>
      <TopNav />
      <BottomNav />

      {/* ✅ ABOVE THE FOLD (render immediately) */}
      <section className="about-landing theme-green">
        <div className="about-landing__bg">
          <span className="about-landing__dots about-landing__dots--tl" />
          <span className="about-landing__dots about-landing__dots--tr" />
          <span className="about-landing__dots about-landing__dots--br" />
        </div>

        <div className="container">
          <div className="about-landing__card shadow-sm">
            <div className="row g-4 align-items-center">
              <div className="col-lg-6">
                <p className="about-landing__eyebrow mb-2">About Us</p>

                <h1
                  className="about-landing__title mb-3"
                  style={{
                    background:
                      "linear-gradient(90deg, #6f4e1e 0%, #cfa44a 18%, #f6e27a 36%, #e0b85c 52%, #b88a2a 68%, #f1d36b 82%, #7a551f 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Pakistan’s Trusted <br /> Real Estate Investment Platform
                </h1>

                <p className="about-landing__lead mb-4">
                  Makkaan.com.pk is Pakistan’s leading real estate investment
                  platform, committed to transparency, trust, and convenience.
                  From residential to commercial projects, we offer expert
                  guidance, complete insights, and a seamless experience — all
                  under one roof.
                </p>

                <div className="d-flex gap-3 flex-wrap">
                  <a
                    href="/projects_list"
                    className="btn btn-theme px-4 py-2 fw-semibold"
                  >
                    Explore Projects
                  </a>
                  <a href="/contact" className="btn btn-outline-theme px-4 py-2">
                    Contact Us
                  </a>
                </div>
              </div>

              {/* ✅ priority image (first view) */}
              <div className="col-lg-6">
                <div className="about-landing__art mx-auto">
                  <ImgWithSkeleton
                    src={makkaanLogo}
                    alt="Makkaan.com.pk"
                    priority={true}
                    className="about-landing__imgwrap"
                    imgStyle={{
                      width: "68%",
                      height: "auto",
                      objectFit: "contain",
                      zIndex: 2,
                    }}
                  />
                  <span className="about-landing__blob about-landing__blob--1" />
                  <span className="about-landing__blob about-landing__blob--2" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ✅ BELOW THE FOLD: mount later + skeleton */}
      <LazySection skeletonHeight={520}>
        {/* ===== CHAIRMAN SECTION ===== */}
        <section className="chairman theme-green">
          <div className="container">
            <div className="row g-4 align-items-center">
              <div className="col-lg-5">
                <div className="chairman__photo-wrap mx-auto">
                  <ImgWithSkeleton
                    src={chairman}
                    alt="Chairman"
                    className="chairman__photoSkel"
                    imgStyle={{
                      width: "114%",
                      height: "114%",
                      objectFit: "cover",
                      borderRadius: "50%",
                      transform: "translateY(-10%)",
                      filter: "drop-shadow(0 14px 26px rgba(0,0,0,.18))",
                    }}
                  />
                  <div className="chairman__tag shadow-sm">
                    <div className="fw-semibold">Mr Aamir Saleem</div>
                    <small className="text-muted">Chairman &amp; Founder</small>
                  </div>
                </div>
              </div>

              <div className="col-lg-7">
                <h2 className="chairman__headline">
                  Welcome Message <br />
                  <span className="text-theme fw-bold">from the Chairman</span>
                </h2>

                <hr className="chairman__rule" />

                <h5 className="fw-bold mb-2">Welcome Message</h5>
                <p className="text-muted mb-0" style={{ lineHeight: 1.9 }}>
                  Our aim is to raise the standard of real estate marketing in
                  Pakistan by ensuring every client feels confident and secure
                  in their investment decisions. With a strong focus on
                  transparency, innovation, and timely delivery, we are
                  committed to building not just projects — but lasting
                  relationships.
                </p>
              </div>
            </div>
          </div>
        </section>
      </LazySection>

      <LazySection skeletonHeight={420}>
        {/* ===== CEO SECTION ===== */}
        <section className="ceo-panel">
          <div className="container">
            <div className="ceo-card rounded-4 shadow-sm">
              <div className="row g-4 align-items-center">
                <div className="col-lg-7">
                  <div className="ceo-card__copy">
                    <div className="ceo-quote-mark">“</div>
                    <h3 className="mb-2 fw-bold text-white">CEO Message</h3>
                    <p className="mb-0 text-white-50" style={{ lineHeight: 1.85 }}>
                      Our vision goes beyond selling property — we’re here to
                      build trust, deliver value, and create secure investment
                      opportunities for both local and overseas clients.
                    </p>
                  </div>
                </div>

                <div className="col-lg-5">
                  <div className="ceo-card__photo-wrap">
                    <ImgWithSkeleton
                      src={hassan}
                      alt="CEO"
                      className="ceo-card__photoSkel"
                      imgStyle={{
                        maxWidth: "min(380px, 38vw)",
                        height: "auto",
                        objectFit: "contain",
                        filter: "drop-shadow(0 18px 30px rgba(0,0,0,0.25))",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </LazySection>

      <LazySection skeletonHeight={520}>
        {/* ===== Makkaan k Mehmaan ===== */}
        <section className="mehman">
          <div className="container">
            <div className="row g-4 align-items-center">
              <div className="col-lg-5">
                <div className="mehman-left">
                  <h2 className="mb-3">
                    Makkaan k <span className="text-theme">Mehmaan</span>
                  </h2>
                  <p className="text-muted mb-0" style={{ lineHeight: 1.85 }}>
                    At <strong>Makkaan k Mehman</strong>, we open doors to
                    meaningful conversations with industry experts, homeowners,
                    architects, and real estate visionaries. From inspiring
                    journeys to behind-the-scenes insights — we talk{" "}
                    <em>real stories</em>, real struggles, and{" "}
                    <strong>real success</strong>.
                  </p>
                </div>
              </div>

              <div className="col-lg-7">
                <div className="mehman-grid">
                  {guests.map((g, i) => (
                    <figure className="mehman-item" key={i} title={`${g.name} — ${g.role}`}>
                      <div className="mehman-photo">
                        <ImgWithSkeleton
                          src={g.img}
                          alt={`${g.name} — ${g.role}`}
                          imgStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </div>
                      <figcaption className="mehman-tag">
                        <strong className="d-block">{g.name}</strong>
                        <small>{g.role}</small>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </LazySection>

      <LazySection skeletonHeight={120}>
        {/* Brand Marquee */}
        <section className="brand-marquee" style={{ marginTop: "50px" }}>
          <div className="brand-marquee__track">
            <span className="brand-marquee__item">
              Makkaan.com.pk — <strong>Smart choices</strong>,{" "}
              <strong>trusted guidance</strong>, <strong>no more stress</strong>
            </span>
            <span className="brand-marquee__item" aria-hidden="true">
              Makkaan.com.pk — <strong>Smart choices</strong>,{" "}
              <strong>trusted guidance</strong>, <strong>no more stress</strong>
            </span>
          </div>
        </section>
      </LazySection>

      <LazySection skeletonHeight={520}>
        {/* ===== Makkaan Development ===== */}
        <section className="dev-promo">
          <div className="container">
            <div className="dev-slab rounded-4 shadow-sm">
              <div className="row g-4 align-items-center">
                <div className="col-lg-7">
                  <p className="dev-kicker mb-2" style={{ color: "#fff" }}>
                    makkaan development
                  </p>
                  <h2 className="dev-title mb-3">On-Time Delivery is in our DNA</h2>
                  <p className="dev-desc mb-0" style={{ color: "#fff" }}>
                    Makkaan has consistently delivered projects on time, building
                    a strong reputation for reliability and customer
                    satisfaction. This commitment led to the creation of
                    <strong> Makkaan Developments</strong> — a dedicated branch
                    focused on continuing this legacy of timely delivery,
                    exceptional quality, and customer value.
                  </p>
                </div>

                <div className="col-lg-5 d-flex align-items-center justify-content-center">
                  <div
                    className="dev-figure dev-figure--logo mx-auto"
                    style={{ width: "100%", maxWidth: "420px" }}
                  >
                    <ImgWithSkeleton
                      src={development}
                      alt="Makkaan Developments"
                      imgStyle={{ width: "80%", height: "auto", objectFit: "contain" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </LazySection>

      <LazySection skeletonHeight={360}>
        {/* ===== SUCCESS STORIES ===== */}
        <section className="success-wide">
          <div className="container-fluid px-0">
            <div className="container">
              <h2 className="success-title text-center mb-4">OUR SUCCESS STORIES</h2>

              <div className="success-logos">
                <ImgWithSkeleton
                  src={peaknest}
                  alt="Peaknest"
                  className="success-logo"
                  imgStyle={{ height: "clamp(40px, 6vw, 72px)", width: "auto" }}
                />
                <span className="success-divider" />
                <ImgWithSkeleton
                  src={ninek}
                  alt="ninek"
                  className="success-logo"
                  imgStyle={{ height: "clamp(40px, 6vw, 72px)", width: "auto" }}
                />
                <span className="success-divider" />
                <ImgWithSkeleton
                  src={developerlogo}
                  alt="27 ARC Link"
                  className="success-logo"
                  imgStyle={{ height: "clamp(40px, 6vw, 72px)", width: "auto" }}
                />
              </div>

              <p className="success-line text-center mt-4">
                <span className="badge-sold">SOLD OUT</span>
                <span className="ms-2">
                  <strong className="text-gold">MAKKAAN.COM.PK</strong> is known for delivering all
                  projects within the promised timeline —{" "}
                  <strong className="text-gold">ON TIME DELIVERED</strong>
                </span>
              </p>
            </div>
          </div>
        </section>
      </LazySection>

      <LazySection skeletonHeight={420}>
        {/* ===== On-Time Guaranteed Delivery ===== */}
        <section className="ontime-solid">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-8 col-md-7 col-12">
                <p className="ontime-kicker mb-2" style={{ color: "#fff" }}>
                  First time in Pakistan
                </p>
                <h2
                  className="ontime-title mb-3"
                  style={{
                    background:
                      "linear-gradient(90deg, #6f4e1e 0%, #cfa44a 18%, #f6e27a 36%, #e0b85c 52%, #b88a2a 68%, #f1d36b 82%, #7a551f 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: "1px",
                    fontFamily: "Parisienne",
                  }}
                >
                  On-Time <span>Guaranteed Delivery</span>
                </h2>

                <p className="ontime-desc mb-0" style={{ color: "#fff" }}>
                  At <strong>Makkaan.com.pk</strong>, we revolutionized the real estate market by
                  introducing Pakistan’s very first <strong>On-Time Guaranteed Delivery</strong>{" "}
                  campaign. We understand that time is money — which is why we don’t just commit to
                  deadlines; we <strong>guarantee</strong> them.
                </p>
              </div>

              <div className="col-lg-4 col-md-5 col-12 text-center mt-4 mb-4 mt-md-0">
                <div className="dev-figure position-relative mx-auto" style={{ width: "100%", maxWidth: "420px" }}>
                  <span className="dev-blob dev-blob--1" aria-hidden="true" />
                  <span className="dev-blob dev-blob--2" aria-hidden="true" />
                  <ImgWithSkeleton
                    src={guranteed}
                    alt="Guaranteed Stamp"
                    imgStyle={{ width: "100%", height: "80%", objectFit: "contain" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </LazySection>

      <Footer />

      {/* ===== Scoped styles ===== */}
      <style>{`
        :root {
          --theme: #4DB749;
          --gold: #D4AF37;
          --gold-70: rgba(212,175,55,0.7);
          --gold-50: rgba(212,175,55,0.5);
          --theme-ink: #124e26;
          --theme-ink-soft: #2e6f3b;
          --theme-10: rgba(77,183,73,0.10);
          --theme-15: rgba(77,183,73,0.15);
          --theme-20: rgba(77,183,73,0.20);
        }

        /* ===== skeletons ===== */
        .section-skeleton{
          background: #fff;
          border: 1px solid rgba(0,0,0,0.06);
          box-shadow: 0 6px 18px rgba(9, 30, 66, 0.06);
          padding: 18px;
          position: relative;
          overflow: hidden;
        }
        .section-skeleton::after{
          content:"";
          position:absolute;
          inset:0;
          transform: translateX(-100%);
          background: linear-gradient(90deg, transparent, rgba(0,0,0,0.04), transparent);
          animation: shimmer 1.2s infinite;
        }
        .section-skeleton__bar{
          height: 12px;
          border-radius: 999px;
          background: rgba(0,0,0,0.06);
          margin: 12px 0;
        }
        @keyframes shimmer{
          100%{ transform: translateX(100%); }
        }

        .img-skel{
          position: relative;
          display: grid;
          place-items: center;
        }
        .img-skel__shimmer{
          position:absolute;
          inset:0;
          border-radius: 12px;
          background: rgba(0,0,0,0.05);
          overflow:hidden;
        }
        .img-skel__shimmer::after{
          content:"";
          position:absolute;
          inset:0;
          transform: translateX(-100%);
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
          animation: shimmer 1.2s infinite;
        }
        .img-skel__img{
          opacity: 0;
          transition: opacity .25s ease;
          display:block;
        }
        .img-skel.is-loaded .img-skel__img{
          opacity: 1;
        }
        .img-skel.is-loaded .img-skel__shimmer{
          display:none;
        }

        .text-theme { color: var(--theme); }
        .text-gold { color: var(--gold); }
        .text-gold-50 { color: var(--gold-50); }
        .btn-theme { background: var(--theme); border-color: var(--theme); color: #fff; }
        .btn-theme:hover { filter: brightness(0.95); color: #fff; }
        .btn-outline-theme { border-color: var(--theme); color: var(--theme); background: #fff; }
        .btn-outline-theme:hover { background: var(--theme); color: #fff; }

        /* Landing banner */
        .about-landing.theme-green { position: relative; padding: clamp(28px, 5vw, 44px) 0; background: linear-gradient(180deg, #ffffff 0%, #f3fbf6 100%); overflow: hidden; }
        .about-landing__bg { position: absolute; inset: 0; pointer-events: none; }
        .about-landing__dots { --size: 120px; width: var(--size); height: var(--size); position: absolute; opacity: .25; background-image: radial-gradient(var(--theme) 2px, transparent 2px); background-size: 12px 12px; filter: drop-shadow(0 2px 0 var(--theme-10)); }
        .about-landing__dots--tl { top: 10px; left: 6%; } .about-landing__dots--tr { top: 24px; right: 10%; } .about-landing__dots--br { bottom: 16px; right: 6%; }
        .about-landing__card { background: #ffffff; border-radius: 18px; padding: clamp(18px, 3.5vw, 32px); position: relative; border: 1px solid rgba(0,0,0,0.06); }
        .about-landing__eyebrow { text-transform: uppercase; letter-spacing: .12em; font-weight: 700; font-size: 12px; color: var(--theme-ink-soft); margin: 0; }
        .about-landing__title { font-weight: 800; line-height: 1.1; font-size: clamp(28px, 3.1vw, 40px); color: #1e2a35; }
        .about-landing__lead { color: #4a5a64; font-size: clamp(14px, 1.15vw, 16px); line-height: 1.8; }
        .about-landing__art { position: relative; width: min(480px, 85%); aspect-ratio: 1.2 / 1; border-radius: 18px; background: #ffffff; border: 1px dashed var(--theme-20); display: grid; place-items: center; overflow: hidden; }
        .about-landing__blob { position: absolute; border-radius: 50%; filter: blur(30px); opacity: .35; }
        .about-landing__blob--1 { width: 260px; height: 260px; background: var(--theme-15); left: -40px; bottom: -50px; }
        .about-landing__blob--2 { width: 220px; height: 220px; background: var(--theme-10); right: -30px; top: -40px; }
        @media (max-width: 991.98px) { .about-landing__art { width: 100%; } }

        /* Chairman */
        .chairman.theme-green { padding: clamp(34px, 6vw, 60px) 0; background: linear-gradient(180deg, #ffffff 0%, #f8fdf9 100%); }
        .chairman__photo-wrap{
          position: relative;
          width: min(360px, 86%);
          aspect-ratio: 1 / 1;
          border-radius: 50%;
          background: #fff;
          display: grid; place-items: center;
          border: 5px solid #fff;
          box-shadow: 0 8px 24px rgba(0,0,0,.06), 0 0 0 8px var(--theme-10);
          overflow: visible;
          z-index: 1;
          isolation: isolate;
        }
        .chairman__photo-wrap::after{
          content:"";
          position:absolute; inset:10%;
          border-radius:50%;
          box-shadow: inset 0 0 0 8px rgba(77,183,73,.12);
          pointer-events:none;
        }
        .chairman__tag{
          position: absolute; left: 0; bottom: -4px; transform: translateX(6%);
          background: #ffffff; border: 1px solid rgba(0,0,0,0.06); border-left: 6px solid var(--theme);
          padding: 10px 14px; border-radius: 12px; min-width: 210px; z-index: 5; box-shadow: 0 8px 18px rgba(0,0,0,.08);
        }
        @media (max-width: 575.98px){
          .chairman__photo-wrap{ width: min(320px, 92%); }
        }
        .chairman__headline { font-size: clamp(28px, 3.4vw, 46px); line-height: 1.12; color: #1e2a35; text-transform: uppercase; letter-spacing: .02em; font-weight: 700; }
        .chairman__rule { border: none; height: 1px; background: linear-gradient(90deg, var(--theme), transparent); margin: 18px 0 16px; opacity: .6; }

        /* CEO */
        .ceo-panel { margin-top: 60px; padding-block: clamp(28px, 6vw, 56px); background: #ffffff; }
        .ceo-card { position: relative; overflow: visible; border: 1px solid rgba(0,0,0,0.06); background: linear-gradient(180deg, #4DB749 0%, #0d5366 100%); padding: clamp(18px, 3.5vw, 32px); }
        .ceo-card::before{ content: ""; position: absolute; inset: 0; background: radial-gradient(1200px 400px at -10% 10%, rgba(255,255,255,0.12), transparent 60%), radial-gradient(800px 800px at 110% 90%, rgba(255,255,255,0.08), transparent 60%); pointer-events: none; z-index: 0; }
        .ceo-card__copy { position: relative; z-index: 1; }
        .ceo-quote-mark { font-size: 72px; line-height: 1; color: rgba(255,255,255,0.25); margin-bottom: 6px; font-weight: 800; }
        .ceo-card__photo-wrap { position: absolute; top: -48px; right: clamp(16px, 3vw, 32px); z-index: 3; width: min(480px, 100%); height: 100%; display: flex; justify-content: flex-end; align-items: flex-end; }
        @media (max-width: 991.98px) {
          .ceo-card { overflow: hidden; padding-right: 24px; }
          .ceo-card__photo-wrap { position: static; margin-top: 0; }
        }

        /* Mehmaan */
        .mehman { padding: clamp(34px, 6vw, 60px) 0; background: #fff; }
        .mehman-left{
          background: radial-gradient(60% 60% at 50% 40%, #f5fbf7 0%, #ffffff 70%);
          border: 1px solid rgba(0,0,0,0.06);
          border-radius: 999px;
          padding: clamp(28px, 4vw, 50px);
          aspect-ratio: 1 / 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        @media (max-width: 991.98px){
          .mehman-left{ aspect-ratio: auto; border-radius: 24px; }
        }
        .mehman-grid{
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 22px;
        }
        @media (max-width: 575.98px){
          .mehman-grid{ grid-template-columns: repeat(2, 1fr); }
        }
        .mehman-item{
          position: relative;
          aspect-ratio: 1 / 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding-bottom: 24px;
        }
        .mehman-photo{
          width: 100%;
          border-radius: 50%;
          overflow: hidden;
          background: #f6f8f9;
          box-shadow: 0 10px 22px rgba(9,30,66,0.08);
        }
        .mehman-tag{
          position: absolute;
          left: 50%;
          bottom: -6px;
          transform: translateX(-50%);
          background: #ffffff;
          color: #1f2a33;
          border-radius: 12px;
          padding: 8px 14px;
          min-width: 75%;
          border: 1px solid rgba(0,0,0,0.06);
          border-left: 5px solid var(--theme);
          box-shadow: 0 8px 18px rgba(0,0,0,.09);
          text-align: left;
          line-height: 1.2;
        }
        .mehman-tag strong{ font-size: 14px; }
        .mehman-tag small{ font-size: 12px; color: #6b7280; }

        /* Dev */
        .dev-promo{ padding: clamp(28px,6vw,56px) 0; }
        .dev-slab{
          position: relative;
          background: linear-gradient(90deg, #4DB749 0%, #070d2aff 100%);
          color: #fff;
          padding: clamp(22px,4vw,36px);
          border: 1px solid rgba(0,0,0,0.06);
          overflow: hidden;
        }
        .dev-slab::before{
          content:"";
          position:absolute;
          inset:0;
          background:
            radial-gradient(900px 400px at -10% 10%, rgba(255,255,255,.14), transparent 60%),
            linear-gradient(0deg, rgba(255,255,255,.05), rgba(255,255,255,.05)),
            repeating-linear-gradient(0deg, rgba(255,255,255,.06), rgba(255,255,255,.06) 1px, transparent 1px, transparent 22px);
          pointer-events:none;
        }
        .dev-kicker{ text-transform: uppercase; letter-spacing: .14em; font-weight: 800; opacity: .95; }
        .dev-title{ font-weight: 900; line-height: 1.1; font-size: clamp(24px,3vw,36px); margin: 0; }
        .dev-desc{ font-size: clamp(14px,1.1vw,16px); line-height: 1.85; opacity: .98; }
        .dev-figure{
          position: relative;
          background: rgba(255,255,255,0.10);
          border: 1px solid rgba(255,255,255,0.28);
          border-radius: 22px;
          padding: min(18px,3vw);
          aspect-ratio: 4 / 3;
          display: grid;
          place-items: center;
          overflow: hidden;
          transform: translateY(10px);
          box-shadow: 0 18px 30px rgba(0,0,0,.12);
        }
        .dev-figure--logo{
          background: rgba(255,255,255,0.25);
          border-color: rgba(255,255,255,0.20);
          transform: none;
        }
        .dev-blob{ position: absolute; border-radius: 50%; filter: blur(26px); opacity: .55; }
        .dev-blob--1{ width: 260px; height: 260px; background: rgba(255,255,255,.30); left: -40px; bottom: -60px; }
        .dev-blob--2{ width: 200px; height: 200px; background: rgba(255,255,255,.20); right: -30px; top: -40px; }

        /* Success */
        .success-wide{ background: transparent; padding: clamp(34px,6vw,70px) 0; color: #1f2a33; }
        .success-title{ color: var(--gold); font-weight: 800; letter-spacing: .04em; }
        .success-logos{ display: flex; align-items: center; justify-content: center; gap: clamp(16px, 4vw, 48px); flex-wrap: wrap; }
        .success-divider{ width: 1px; height: clamp(28px, 4vw, 44px); background: rgba(0,0,0,.08); display: inline-block; }
        .success-line{ font-size: clamp(14px, 1.15vw, 16px); line-height: 1.8; }
        .badge-sold{
          display:inline-block;
          font-weight:800;
          text-transform:uppercase;
          letter-spacing:.06em;
          background:#000;
          color:#fff;
          border-radius:12px;
          padding:6px 10px;
          border:2px solid #fff;
          box-shadow: 0 6px 14px rgba(0,0,0,.12);
        }

        /* On-Time */
        .ontime-solid{
          background: #070d2aff;
          color: #ffffff;
          padding: clamp(36px, 8vw, 72px) 0;
          position: relative;
          overflow: hidden;
        }
        .ontime-solid::before{
          content:"";
          position:absolute;
          inset:0;
          background:
            radial-gradient(600px 200px at -10% 0%, rgba(255,255,255,.12), transparent 60%),
            radial-gradient(500px 400px at 110% 100%, rgba(255,255,255,.10), transparent 60%);
          pointer-events:none;
        }
        .ontime-kicker{ text-transform: uppercase; letter-spacing: .12em; font-weight: 800; opacity: .95; }
        .ontime-title{ font-weight: 900; line-height: 1.1; font-size: clamp(26px, 3.4vw, 44px); margin: 0; }
        .ontime-desc{ font-size: clamp(14px, 1.15vw, 16px); line-height: 1.9; opacity: .98; }

        /* Marquee */
        .brand-marquee {
          --marquee-speed: 28s;
          --marquee-gap: 4rem;
          background: #070d2aff;
          color: #e9fff3;
          border-top: 1px solid rgba(255,255,255,0.06);
          border-bottom: 1px solid rgba(255,255,255,0.06);
          overflow: hidden;
          padding: 14px 0;
        }
        .brand-marquee__track {
          display: flex;
          width: max-content;
          gap: var(--marquee-gap);
          white-space: nowrap;
          will-change: transform;
          animation: marqueeScroll var(--marquee-speed) linear infinite;
        }
        .brand-marquee:hover .brand-marquee__track { animation-play-state: paused; }
        .brand-marquee__item {
          font-size: clamp(16px, 1.4vw, 18px);
          letter-spacing: .2px;
          opacity: .98;
        }
        .brand-marquee__item strong { color: var(--theme); font-weight: 700; }
        @keyframes marqueeScroll {
          from { transform: translateX(0); }
          to { transform: translateX(calc(-100% - var(--marquee-gap))); }
        }
      `}</style>
    </>
  );
}